// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import Promise from 'bluebird';
import _ from 'underscore';
import myw from 'myWorld/base/core';
import detailsTabHtml from 'text!html/detailsTab.html';
import './feature/featureViewer';
import './feature/featureEditor';
import './resultsListControl';
import './traceResultControl';
import 'myWorld/base/plugin';
import 'myWorld/uiComponents/toolbar';
import './editButton';

myw.DetailsControl = myw.Control.extend('DetailsControl', {
    options: {
        usePopupEditor: undefined,
        navButtons: ['application.prevResult', 'application.nextResult'],
        featureButtons: ['results-list', 'edit', 'zoom'],
        resultsButtons: ['zoom-all'],
        viewersState: {
            //state that will be shared across FeatureViewer instances
            attributeDisplayMode: 'medium',
            collapsed: false
        },
        DefaultFeatureViewer: myw.FeatureViewer
    },

    //shared properties (stored in the prototype)
    topLevelContainerIds: [
        'no-detail-contents',
        'details-loading',
        'feature-details',
        'feature-editor',
        'results-list',
        'trace-result'
    ],

    messageGroup: 'details',

    /**
     * @class   Control to display feature details and display results.
     * Manages 5 panes of which one is displayed: "details-loading", "feature-details", "feature-editor", "no-detail-contents", "results-list".
     *
     * Displaying of feature details can be customized by registering sub-classes of myw.FeatureViewer with feature models.
     * Feature editing can be customized  by registering sub-classes of myw.FeatureEditor with feature models. See {@link Feature}
     *
     * The panel below that exists below the one that displays the feature details is customizable by registering plugins with the pluginIds parameter. Example: </br>
     * <b>   new myw.DetailsControl(tabControl, { pluginIds: ["notes"] })</b> <br/>
     * These plugins have to be previously registered with the application and need to then <br/>
     * implement the following method: <br/>
     * <b>updateFeatureDetailsDivFor: (feature, parentDiv) => {...}  </b><br/>
     *      This method is called after displaying the field values, so the plugin can add a new section or change the existing DOM elements
     *
     *
     * @param  {Application|Control}    owner       Owner of self
     * @param  {detailsControlOptions}          options
     * @constructs
     */
    initialize(owner, options) {
        myw.Control.prototype.initialize.call(this, owner, options);

        this.currentSelectionOrigin = null;

        //a list of the application plugins used by DetailsControl, for quicker access
        this.plugins = _.pick(this.app.plugins, options.pluginIds);
        this.pluginExtras = {};

        this.initUI();

        this.initAppEventHandlers();

        /**Tracks the locked state editor should be in.
         * @type {boolean} */
        this.isEditorLocked = false;
    },

    /**
     * Initial render of UI
     */
    initUI() {
        this.$el.html(detailsTabHtml);
        myw.translate('details', this.$el);

        this.createToolbars();

        this.resultsListControl = new myw.ResultsListControl(this, { el: this.$('#results-list') });
        this.traceResultControl = new myw.TraceResultControl(this, { el: this.$('#trace-result') });
        //initial state is no details
        this.showNoDetailsMessage();

        this._setPanelContentHeight();
    },

    createToolbars() {
        const navButtonWidth = 40;
        const leftToolbarWidth = this.options.navButtons.length * navButtonWidth;
        const rightToolbarWidth = this.owner.$el.width() - leftToolbarWidth - 20;

        //create buttons to navigate backwards and forwards in the browsed features stack
        this.addButtons.call(
            this,
            this.$('#results-nav'),
            this.options.navButtons,
            {},
            leftToolbarWidth,
            navButtonWidth
        );
        //create buttons for when displaying a feature's details
        this.addButtons.call(
            this,
            this.$('#details-actions'),
            this.options.featureButtons,
            {},
            rightToolbarWidth,
            navButtonWidth
        );
        //create buttons for when displaying a list of results
        this.addButtons.call(
            this,
            this.$('#multiple-details-actions'),
            this.options.resultsButtons,
            {},
            rightToolbarWidth,
            navButtonWidth
        );
    },

    /**
     * Setup handlers for application events
     */
    initAppEventHandlers() {
        const app = this.app;

        app.on('selection-started', data => {
            //a selection has started, so active the 'loading...' panel
            this.currentSelectionOrigin = data.origin; //the origin is necessary to adjust the message when nothing is found
            this.showTopLevelContainer('details-loading');
        });

        app.on('selection-cleared', data => {
            this.currentSelectionOrigin = 'clear_results';
        });

        app.on('query-started', data => {
            this.currentSelectionOrigin = 'search';
            this.showTopLevelContainer('details-loading');
        });

        app.on('currentFeatureSet-changed currentFeature-changed', e => {
            //new feature(s),
            this._featureEvent = e;
            const currentFeatureType = e.feature && e.feature.getType();
            const mode = this.app.displayModeFor(currentFeatureType);
            this.setMode(mode); //leaves 'edit' mode if necessary
        });
    },

    /**
     * Sets the current mode of the control, and updates the UI
     * @param {string} mode 'view' or 'edit'
     */
    setMode(mode) {
        const switchedMode = this.mode && this.mode !== mode;
        this.mode = mode;
        const msg = this.msg('feature_editor');
        if (mode == 'edit') {
            //If the app is in edit mode,
            //we'll always have to end the current transaction before starting a new one
            if (this.app.editMode) this.app.database.endUserTransaction();

            return this.app.database
                .beginUserTransaction(msg)
                .then(() => {
                    this.render();
                })
                .catch(error => {
                    new myw.Dialog({
                        contents: error.message
                    });
                    console.log(error);
                });
        } else {
            if (switchedMode) this.app.database.endUserTransaction();
            this.render();
        }
    },

    /**
     * Updates the UI for the current state
     */
    render() {
        const currentFeatureSet = this.app.currentFeatureSet;
        const currentFeature = this.app.currentFeature;

        if (this.editor) {
            // we were editing a feature but are now displaying something different - disable edit mode
            this.editor.close();
            this.editor = null;
        }

        if (currentFeature === null && currentFeatureSet.isEmpty()) {
            this.showNoDetailsMessage();
        } else if (currentFeature !== null) {
            if (currentFeature.isNew || (this.mode == 'edit' && currentFeature.isEditable())) {
                this.setCurrentFeatureEditable();
            } else {
                //ENH: ensure necessary feature properties are available before continuing (instead of assuming it has been done by the application)
                this.displayFeatureDetails(currentFeature);
            }
        } else {
            // Populate the multiple results container from the current feature set.
            if (currentFeatureSet.isTraceResult) this.displayTraceResult();
            else this.populateMultipleResults();
        }
        this.trigger('change'); //trigger event so buttons update themselves
    },

    /**
     * Show the no details message
     */
    showNoDetailsMessage() {
        // Reset the visibility of the top level detail containers.
        this.showTopLevelContainer('no-detail-contents', false);

        //hide the details actions and multiple-details actions from the navigation bar
        this.$('#details-actions, .multiple-details-actions').hide();

        let msgId = `no_details_${this.currentSelectionOrigin || 'default'}`;

        if (
            this.currentSelectionOrigin === 'map' &&
            this.app.map.getZoom() < this.app.map.getMaxZoom()
        ) {
            msgId = 'no_details_map_zoom_closer';
        }
        const msg = this.currentSelectionOrigin === 'clear_results' ? '' : this.msg(msgId);
        this.$('#no-detail-contents-message').html(msg);
    },

    /**
     * Display the details of a feature
     * @param  {Feature} feature
     */
    displayFeatureDetails(feature) {
        this.showTopLevelContainer('feature-details');
        const viewer = this._getViewerFor(feature);
        this.$('#feature-viewer').html(viewer.el);
        viewer.displayFeatureDetails(feature);

        this._renderPluginExtras(feature);
    },

    /*
     * Returns a feature viewer appropriate for the given feature
     * Caches instances of the different viewer classes
     * @param  {Feature} feature
     * @return {FeatureViewer}
     */
    _getViewerFor(feature) {
        if (feature.viewerClass) {
            const type = feature.getType();
            if (!this._viewers) this._viewers = {};

            if (!this._viewers[type]) {
                this._viewers[type] = new feature.viewerClass(this, {
                    state: this.options.viewersState
                });
            }
            return this._viewers[type];
        } else {
            if (!this._defaultViewer) {
                const viewerOptions = { state: this.options.viewersState };
                this._defaultViewer = new this.options.DefaultFeatureViewer(this, viewerOptions);
            }
            return this._defaultViewer;
        }
    },

    /**
     * Starts edit mode for the current feature of the application
     */
    setCurrentFeatureEditable() {
        const feature = this.app.currentFeature;
        let refreshPromise;
        if (feature.isNew) {
            //detached feature, no need to refresh..
            refreshPromise = Promise.resolve(feature);
        } else {
            //refresh feature details. also ensures we edit the feature and not a "qualified" feature with a
            // partial geometry as it happens when we start from a trace node
            refreshPromise = feature.datasource
                .getFeatureByUrn(feature.getUrn(), true, feature.getDelta())
                .catch(myw.ObjectNotFoundError, error => {
                    this.app.setCurrentFeature(null);
                    this.app.message(this.app.msg('missing_object_error'));
                });
        }

        return refreshPromise.then(feature => {
            if (!feature) return;
            this.createEditorFor(feature);
        });
    },

    createEditorFor(feature) {
        const usePopup = myw.isTouchDevice || feature.usePopupEditor || this.options.usePopupEditor;
        const editorOptions = {
            feature: feature,
            map: this.app.map,
            useTabs: usePopup,
            useExpandedFieldEditors: usePopup
        };
        let Editor;

        if (!usePopup) {
            this.showTopLevelContainer('feature-editor');
            this.$('#detail-contents').scrollTop(0);
            editorOptions.el = $('#feature-editor');
        }

        Editor = feature.editorClass || myw.FeatureEditor;
        this.editor = new Editor(this, editorOptions);
        this.editor.once('cancelled', this._editorClosed, this);
        this.editor.once('created_not_accessible', msg => {
            this.app.message(msg);
            this.app.setCurrentFeature();
        });

        this.editor.once('saved', featureProps => {
            this.handleSavedFeature(featureProps);
        });

        this.trigger('change');
    },

    async handleSavedFeature(featureProps) {
        const feature = featureProps.feature;
        await this.app.setCurrentFeature(feature);
        this.isEditorLocked = featureProps.isLocked;
        if (featureProps.isLocked) {
            //Create a new feature with the properties of the freshly created feature.
            const detachedFeature = await this.app.database.createDetachedFeature(
                feature.getType(),
                true
            );

            detachedFeature.properties = { ...feature.properties };
            //Remove the keyField property because we are creating a new feature
            delete detachedFeature.properties[feature.keyFieldName];

            this.app.setCurrentFeature(detachedFeature);
        }
    },

    /**
     * Populate the multiple results container.
     */
    populateMultipleResults() {
        this.showTopLevelContainer('results-list');
        this.resultsListControl.render();
    },

    displayTraceResult() {
        this.showTopLevelContainer('trace-result');
        this.traceResultControl.render();
    },

    /**
     * Shows paneIdToShow and hides the other main panes
     * @param  {boolean} [ensureVisible=true]  Whether to ensure self is visible
     * @param  {string} paneIdToShow
     */
    showTopLevelContainer(paneToShow, ensureVisible = true) {
        this.$('.list-export').removeClass('inactive');
        if (!this.$el.is(':visible') && ensureVisible) {
            this._activate();
        }

        //update buttons state
        if (['feature-details', 'feature-editor'].includes(paneToShow)) {
            this.$('#details-actions').show();
            this.$('.multiple-details-actions').hide();
        } else if (paneToShow == 'results-list' || paneToShow == 'trace-result') {
            //hide the 'click to go back to the list of other features' button
            this.$('#details-actions').hide();
            this.$('.multiple-details-actions').show();
        }

        _.each(this.topLevelContainerIds, paneId => {
            if (paneId != paneToShow) {
                if (paneId == 'results-list') {
                    this.resultsListControl.hide();
                } else if (paneId == 'trace-result') {
                    this.traceResultControl.hide();
                } else {
                    this.$(`#${paneId}`).hide();
                }
            }
        });

        //to avoid flicker, only show the container we want after the others have been hidden
        this.$(`#${paneToShow}`).show();
        this._setPanelContentHeight();
    },

    /**
     * Makes self visible by requesting it to the owning framework
     * @private
     */
    _activate() {
        this.owner.fire('activateControl', { control: this });
    },

    /**
     * Looks through all the plugins and
     * calls the visibilityChanged() method on them if available.
     * @param  {Boolean} isVisible
     */
    visibilityChanged(isVisible) {
        for (const name in this.plugins) {
            const plugin = this.plugins[name];
            if (plugin.visibilityChanged) plugin.visibilityChanged(isVisible);
        }
        this._setPanelContentHeight();
    },

    /**
     * Called by the owner when the available area for self changes
     * Adjusts the height of the results table and details-content div so that scrolling works correctly
     * Adjusts the size of the plugins panels
     */
    invalidateSize() {
        this._setPanelContentHeight();
        for (const name in this.plugins) {
            const plugin = this.plugins[name];
            if (plugin.invalidateSize) plugin.invalidateSize();
        }
        this.createToolbars();
        this.resultsListControl.invalidateSize();
        this.traceResultControl.invalidateSize();
    },

    getState() {
        return {
            viewersState: this.options.viewersState
        };
    },

    _renderPluginExtras(feature) {
        //check if the registered plugins want to add something
        this.$('#feature-plugins-details').show();
        for (const name in this.plugins) {
            const plugin = this.plugins[name];
            if (plugin.updateFeatureDetailsDivFor) {
                const pluginContainer = this._getPluginExtraContainer(name);
                plugin.updateFeatureDetailsDivFor(feature, pluginContainer, this._featureEvent);
            }
        }
    },

    /*
     * Returns the container for the plugin extra. If it does not exist creates one.
     * @param  {string} pluginName
     * @return {jQueryElement}
     */
    _getPluginExtraContainer(pluginName) {
        if (!this.pluginExtras[pluginName]) {
            const pluginsDiv = this.$('#feature-plugins-details');
            const insertIndex = _.indexOf(this.options.pluginIds, pluginName);
            const pluginContainer = $('<div>');

            if (insertIndex > 0) {
                const prevContainer = pluginsDiv.children()[insertIndex - 1];
                this.$(prevContainer).after(pluginContainer);
            } else {
                pluginsDiv.append(pluginContainer);
            }
            this.pluginExtras[pluginName] = pluginContainer;
        }
        return this.pluginExtras[pluginName];
    },

    /**
     * Handler for when the feature editor is closed.
     * @private
     */
    _editorClosed() {
        if (!this.app.currentFeature || this.app.currentFeature.isNew) {
            //was creating a feature, navigate back
            this.app.featureNavigation.updateResults('previous');
            this.isEditorLocked = false;
        } else {
            //was editing an existing feature, display it in readonly mode
            this.setMode('view');
        }
        this.editor = null;
    },

    /**
     * Sets the heights of the various components in the feature details panel
     * This is required esp. to support all screen sizes and the use of virtual keyboard on touch devices
     * @private
     */
    _setPanelContentHeight() {
        const container = this.$el;

        if (!container.is(':visible')) return; //Don't bother resizing if the tab panel is hidden

        const navBarHeight = $('.navigation-bar').outerHeight(),
            panelContentHeight = container.height() - navBarHeight;

        this.$('#feature-details').height(panelContentHeight);
        this.$('#feature-editor').height(panelContentHeight);
    }
});

const PluginButton = myw.PluginButton;

const ZoomToAllButton = PluginButton.extend({
    className: 'zoom-all-details',
    imgSrc: 'images/actions/zoom.svg',
    titleMsg: 'zoom_all',
    action() {
        this.app.recordFunctionalityAccess('core.details_tab.zoom_all');
        this.app.map.fitBoundsToFeatures(this.app.currentFeatureSet.items);
    }
});

const ResultsListButton = PluginButton.extend({
    id: 'results-list-button',
    imgSrc: 'images/actions/list.svg',
    inactiveImgSrc: 'images/actions/list.svg',
    titleMsg: 'current_results',
    action() {
        this.app.recordFunctionalityAccess('core.details_tab.list');
        this.app.setCurrentFeature(null, null, true);
    },
    render() {
        const listAvailable = this.app.currentFeatureSet.items.length > 1;
        this.setActive(listAvailable);
    }
});

const ZoomButton = PluginButton.extend({
    id: 'details-zoom',
    imgSrc: 'images/actions/zoom.svg',
    titleMsg: 'zoom_to',
    action() {
        this.app.recordFunctionalityAccess('core.details_tab.zoom_to');
        this.app.map.zoomTo(this.app.currentFeature);
    },
    render() {
        const feature = this.app.currentFeature,
            active = feature && feature.getGeometryInWorld('geo');
        this.setActive(active);
    }
});

myw.DetailsControl.prototype.buttons = {
    'results-list': ResultsListButton,
    edit: myw.EditButton,
    zoom: ZoomButton,
    'zoom-all': ZoomToAllButton
};

/**
 * @typedef detailsControlOptions
 * @property {boolean}          usePopupEditor  Whether feature editors should be used in popup mode or not. <br/>
 *                                              By default, popup mode will be used on touch devices and also for feature models that specify it by setting the property 'usePopupEditor' to True
 * @property {Array<string>}    pluginIds       List of plugins that may provide addtional feature details by implementing method updateFeatureDetailsDivFor(feature, parentDiv)
 * @property {Array<buttonId>}  featureButtons   List of buttons to use when displaying details of a feature
 * @property {Array<buttonId>}  resultsButtons   List of buttons to use when displaying a list of results
 * @property {object}           viewersState    State that will be shared across FeatureViewer instances. Will be passed to the viewer constructor as the 'state' option
 */

export default myw.DetailsControl;
